import React from 'react';
import { Badge as FlowbiteBadge } from 'flowbite-react';
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
  IconClock,
  IconTrendingUp,
  IconTrendingDown
} from '@tabler/icons-react';

/**
 * Badge component - Status indicators with MatDash styling
 * Extends Flowbite Badge with custom theme colors
 */
const Badge = ({
  children,
  color = 'default',
  size = 'md',
  icon = null,
  rounded = 'full',
  className = '',
  ...props
}) => {
  // MatDash color scheme
  const colorClasses = {
    default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    primary: 'bg-primary-light text-gray-900 border border-primary',  // Green theme
    success: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    danger: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    dark: 'bg-gray-800 text-gray-100 dark:bg-gray-900 dark:text-gray-100',
  };

  const sizeClasses = {
    xs: 'text-xs px-2 py-0.5',
    sm: 'text-sm px-2.5 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5',
  };

  const roundedClasses = {
    full: 'rounded-full',
    lg: 'rounded-lg',
    md: 'rounded-md',
    none: 'rounded-none',
  };

  const baseClasses = `
    inline-flex items-center font-medium
    ${colorClasses[color] || colorClasses.default}
    ${sizeClasses[size] || sizeClasses.md}
    ${roundedClasses[rounded] || roundedClasses.full}
    ${className}
  `;

  return (
    <span className={baseClasses} {...props}>
      {icon && (
        <span className="mr-1.5">
          {icon}
        </span>
      )}
      {children}
    </span>
  );
};

// Status Badge Variants
export const StatusBadge = ({ status, ...props }) => {
  const statusConfig = {
    active: { color: 'success', icon: <IconCheck size={14} /> },
    inactive: { color: 'default', icon: <IconX size={14} /> },
    pending: { color: 'warning', icon: <IconClock size={14} /> },
    error: { color: 'danger', icon: <IconAlertTriangle size={14} /> },
    info: { color: 'info', icon: <IconInfoCircle size={14} /> },
  };

  const config = statusConfig[status] || statusConfig.info;

  return (
    <Badge color={config.color} icon={config.icon} {...props}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

// Trend Badge
export const TrendBadge = ({ value, showIcon = true, ...props }) => {
  const isPositive = value >= 0;
  const color = isPositive ? 'success' : 'danger';
  const icon = showIcon ? (
    isPositive ? <IconTrendingUp size={14} /> : <IconTrendingDown size={14} />
  ) : null;

  return (
    <Badge color={color} icon={icon} {...props}>
      {isPositive ? '+' : ''}{value}%
    </Badge>
  );
};

// Count Badge
export const CountBadge = ({ count, maxCount = 99, ...props }) => {
  const displayCount = count > maxCount ? `${maxCount}+` : count;

  return (
    <Badge color="primary" size="sm" {...props}>
      {displayCount}
    </Badge>
  );
};

// Engine Badge (for AI engines)
export const EngineBadge = ({ engine, ...props }) => {
  const engineColors = {
    claude: 'primary',
    chatgpt: 'success',
    gemini: 'info',
    perplexity: 'warning',
  };

  const color = engineColors[engine?.toLowerCase()] || 'default';

  return (
    <Badge color={color} rounded="md" {...props}>
      {engine}
    </Badge>
  );
};

// Score Badge
export const ScoreBadge = ({ score, ...props }) => {
  let color = 'default';

  if (score >= 80) color = 'success';
  else if (score >= 60) color = 'primary';
  else if (score >= 40) color = 'warning';
  else if (score > 0) color = 'danger';

  return (
    <Badge color={color} {...props}>
      {score}%
    </Badge>
  );
};

export default Badge;